import React, { useState, useRef, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import {
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  Chip,
  InputBase,
  RadioGroup,
  FormControlLabel,
  Radio,
  InputAdornment,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@material-ui/core';
import { 
  Thermometer, 
  Target, 
  Lightning as Zap, 
  Clock, 
  Tag as Tags, 
  Fire as Flame, 
  FloppyDisk as Save,
  Plus,
  Trash,
  Briefcase,
  EnvelopeSimple,
  CurrencyDollar,
  ChatText,
  Microphone,
  ShieldCheck
} from '@phosphor-icons/react';
import api from '../../services/api';
import { toast } from 'react-toastify';

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: '100vh',
    backgroundColor: theme.palette.background.default,
    fontFamily: '"Poppins", sans-serif',
    paddingBottom: theme.spacing(6),
    overflowY: 'auto',
  },
  header: {
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
    position: 'sticky',
    top: 0,
    zIndex: 10,
    boxShadow: theme.shadows[1],
  },
  headerContent: {
    maxWidth: 900,
    margin: '0 auto',
    padding: theme.spacing(2, 3),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  container: {
    maxWidth: 900,
    margin: '0 auto',
    padding: theme.spacing(4, 3),
  },
  sectionPaper: {
    marginBottom: theme.spacing(4),
    borderRadius: 16,
    overflow: 'hidden',
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
  },
  sectionHeader: {
    backgroundColor: theme.palette.type === 'light' ? '#f8fafc' : theme.palette.background.default,
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(2.5, 3),
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
  },
  iconBox: {
    backgroundColor: theme.palette.background.paper,
    padding: '10px',
    borderRadius: '12px',
    display: 'flex',
    border: `1px solid ${theme.palette.divider}`,
    color: theme.palette.primary.main,
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  sectionBody: {
    padding: theme.spacing(3),
  },
  chipInputContainer: {
    border: `1px solid ${theme.palette.type === 'light' ? '#c4c4c4' : '#555'}`,
    borderRadius: 8,
    padding: '8px',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    alignItems: 'flex-start',
    alignContent: 'flex-start',
    flex: 1,
    cursor: 'text',
    overflowY: 'auto',
    '&::-webkit-scrollbar': {
      width: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: theme.palette.action.disabled,
      borderRadius: '2px',
    },
  },
  tagsRuleContainer: {
    border: `1px solid ${theme.palette.type === 'light' ? '#e5e7eb' : '#333'}`,
    borderRadius: 8,
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    marginTop: '8px',
    overflow: 'hidden',
  },
  tagsRuleHeader: {
    backgroundColor: theme.palette.type === 'light' ? '#f9fafb' : '#1e1e1e',
    borderBottom: `1px solid ${theme.palette.type === 'light' ? '#e5e7eb' : '#333'}`,
  },
  tagsRuleFooter: {
    backgroundColor: theme.palette.type === 'light' ? '#f9fafb' : '#1e1e1e',
    borderTop: `1px solid ${theme.palette.type === 'light' ? '#e5e7eb' : '#333'}`,
  },
  reminderBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: 8,
    backgroundColor: theme.palette.type === 'light' ? 'rgba(234, 88, 12, 0.09)' : 'rgba(34, 197, 94, 0.09)',
    border: `1px solid ${theme.palette.type === 'light' ? 'rgba(234, 88, 12, 0.09)' : 'rgba(34, 197, 94, 0.09)'}`,
  },
  reminderText: {
    color: theme.palette.type === 'light' ? '#ea580c' : '#4ade80',
    fontWeight: 500,
  },
  autoRuleCard: {
    backgroundColor: theme.palette.type === 'light' ? '#f8fafc' : '#1e1e1e',
    border: `1px solid ${theme.palette.type === 'light' ? '#e2e8f0' : '#333'}`,
    borderRadius: '12px',
    padding: theme.spacing(2),
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    flex: '1 1 calc(33.333% - 16px)',
    minWidth: '200px',
  },
  autoRuleIcon: {
    backgroundColor: theme.palette.type === 'light' ? '#e0f2fe' : 'rgba(56, 189, 248, 0.1)',
    color: theme.palette.type === 'light' ? '#0284c7' : '#38bdf8',
    padding: '8px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

// Componente auxiliar para seções
const ConfigSection = ({ icon: Icon, title, subtitle, children, classes }) => {
  return (
    <Paper elevation={0} className={classes.sectionPaper}>
      <Box className={classes.sectionHeader}>
        <div className={classes.iconBox}>
          <Icon size={24} weight="regular" />
        </div>
        <Box>
          <Typography variant="h6" style={{ fontWeight: 600, fontSize: '1.1rem' }}>{title}</Typography>
          <Typography variant="body2" color="textSecondary">{subtitle}</Typography>
        </Box>
      </Box>
      <Box className={classes.sectionBody}>
        {children}
      </Box>
    </Paper>
  );
};

// Componente auxiliar para rótulos de campos
const Label = ({ title, description, mbStyle }) => (
  <Box mb={1} style={mbStyle}>
    <Typography variant="subtitle2" style={{ fontWeight: 600 }}>{title}</Typography>
    {description && <Typography variant="caption" color="textSecondary">{description}</Typography>}
  </Box>
);

export default function LeadScoring() {
  const history = useHistory();
  const classes = useStyles();
  const theme = useTheme();
  const inputRef = useRef(null);
  
  // Estados para simular a interatividade
  const [keywords, setKeywords] = useState(["comprar", "preço", "orçamento"]);
  const [newKeyword, setNewKeyword] = useState("");
  
  const [lowKeywords, setLowKeywords] = useState(["caro", "inviável", "cancelar"]);
  const [newLowKeyword, setNewLowKeyword] = useState("");
  
  // Estados do Modal de Tag
  const [tagRuleModalOpen, setTagRuleModalOpen] = useState(false);
  const [tagRuleForm, setTagRuleForm] = useState({
    name: '',
    color: '#2563eb',
    action: 'points',
    points: 10
  });

  const [config, setConfig] = useState({
    inboundTemperature: 'hot',
    bonusAds: 20,
    pendingDecayRate: 5,
    commonDecayRate: 2,
    slaFirstResponse: 15,
    slaFollowUp: 24,
    criticalCooldownHours: 48,
    callAlert: 3,
    tagWeights: [],
    funnelStepAccelerator: 'nenhuma'
  });

  // Carregar os dados reais do Backend ao abrir a tela
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const { data } = await api.get('/lead-scoring');
        if (data && data.id) {
          setConfig({
            inboundTemperature: data.inboundTemperature || 'hot',
            bonusAds: data.bonusAds !== undefined ? data.bonusAds : 20,
            pendingDecayRate: data.pendingDecayRate !== undefined ? data.pendingDecayRate : 5,
            commonDecayRate: data.commonDecayRate !== undefined ? data.commonDecayRate : 2,
            slaFirstResponse: data.slaFirstResponse || 15,
            slaFollowUp: data.slaFollowUp || 24,
            criticalCooldownHours: data.criticalCooldownHours || 48,
            callAlert: data.callAlert || 3,
            tagWeights: data.tagWeights || [],
            funnelStepAccelerator: data.funnelStepAccelerator || 'nenhuma'
          });
          if (data.highIntentKeywords) {
            setKeywords(data.highIntentKeywords);
          }
          if (data.lowIntentKeywords) {
            setLowKeywords(data.lowIntentKeywords);
          }
        }
      } catch (err) {
        console.error(err);
        toast.error('Erro ao carregar configurações de Lead Scoring.');
      }
    };
    fetchConfig();
  }, []);

  const addKeyword = (e) => {
    if (e.key === 'Enter' && newKeyword.trim() !== '') {
      setKeywords([...keywords, newKeyword.trim()]);
      setNewKeyword("");
    }
  };

  const removeKeyword = (index) => {
    setKeywords(keywords.filter((_, i) => i !== index));
  };

  const addLowKeyword = (e) => {
    if (e.key === 'Enter' && newLowKeyword.trim() !== '') {
      setLowKeywords([...lowKeywords, newLowKeyword.trim()]);
      setNewLowKeyword("");
    }
  };

  const removeLowKeyword = (index) => {
    setLowKeywords(lowKeywords.filter((_, i) => i !== index));
  };

  // Gerenciamento de Regras de Tags
  const handleOpenTagRuleModal = () => {
    setTagRuleForm({ name: '', color: '#2563eb', action: 'points', points: 10 });
    setTagRuleModalOpen(true);
  };

  const handleSaveTagRule = async () => {
    if (!tagRuleForm.name.trim()) {
      toast.error('O nome da tag é obrigatório.');
      return;
    }
    
    try {
      // Tenta criar a tag globalmente para garantir que ela exista no sistema
      await api.post('/tags', { 
        name: tagRuleForm.name.trim(), 
        color: tagRuleForm.color, 
        kanban: 0 
      });
    } catch (e) {
      console.log('Tag já existente ou erro silencioso', e);
    }

    const newTagWeights = [...(config.tagWeights || []), tagRuleForm];
    setConfig({ ...config, tagWeights: newTagWeights });
    setTagRuleModalOpen(false);
  };

  const handleRemoveTagRule = (index) => {
    const newTagWeights = [...(config.tagWeights || [])];
    newTagWeights.splice(index, 1);
    setConfig({ ...config, tagWeights: newTagWeights });
  };

  const handleSave = async () => {
    try {
      const payload = {
        ...config,
        highIntentKeywords: keywords,
        lowIntentKeywords: lowKeywords
      };
      await api.post('/lead-scoring', payload);
      toast.success('Regras de pontuação salvas com sucesso!');
    } catch (err) {
      console.error(err);
      toast.error('Erro ao salvar as configurações.');
    }
  };

  return (
    <div className={classes.root}>
      
      {/* Cabeçalho Superior Fixo */}
      <div className={classes.header}>
        <div className={classes.headerContent}>
          <Box display="flex" alignItems="center" style={{ gap: '12px' }}>
            <Box style={{ backgroundColor: '#ffedd5', color: '#ea580c', padding: '8px', borderRadius: '8px', display: 'flex' }}>
              <Thermometer size={24} weight="bold" />
            </Box>
            <Box>
              <Typography variant="h6" style={{ fontWeight: 'bold', lineHeight: 1.2 }}>Motor de Temperatura</Typography>
              <Typography variant="body2" color="textSecondary">Configure como os leads ganham e perdem pontos</Typography>
            </Box>
          </Box>
          <Button 
            variant="contained"
            color="primary"
            onClick={handleSave}
            startIcon={<Save size={18} weight="bold" />}
            style={{ borderRadius: 8, padding: '8px 20px', fontWeight: 600, textTransform: 'none' }}
          >
            Salvar Regras
          </Button>
        </div>
      </div>

      <div className={classes.container}>
        
        {/* Seção 1: Origem e Intenção Inicial */}
        <ConfigSection 
          icon={Target} 
          title="1. Origem e Intenção Inicial" 
          subtitle="Define o 'Score Base' do lead assim que ele entra no sistema, antes da primeira interação."
          classes={classes}
        >
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Label 
                title="Temperatura Base Inbound" 
                description="Como um lead é pontuado ao iniciar a conversa com sua empresa."
              />
              <Box display="flex" flexDirection="column" style={{ gap: '16px', marginTop: '8px' }}>
                <TextField 
                  label="Score de Novo Contato" select fullWidth variant="outlined" size="small" SelectProps={{ native: true }} InputLabelProps={{ shrink: true }}
                  value={config.inboundTemperature}
                  onChange={(e) => setConfig({ ...config, inboundTemperature: e.target.value })}
                >
                  <option value="hot">Quente (70 pontos)</option>
                  <option value="warm">Morno (50 pontos)</option>
                  <option value="cold">Frio (20 pontos)</option>
                </TextField>
                <TextField 
                  label="Bônus Origem Ads (Tráfego Pago)" type="number" fullWidth variant="outlined" size="small" 
                  value={config.bonusAds}
                  onChange={(e) => setConfig({ ...config, bonusAds: Number(e.target.value) })}
                  InputProps={{ endAdornment: <InputAdornment position="end">Pontos</InputAdornment> }}
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={6} style={{ display: 'flex', flexDirection: 'column' }}>
              <Label 
                title="Palavras-chave de Alta Intenção" 
                description="Palavras na mensagem inicial que disparam o score. Pressione Enter para adicionar."
              />
              <div 
                className={classes.chipInputContainer} 
                onClick={() => inputRef.current && inputRef.current.focus()}
              >
                {keywords.map((kw, i) => (
                  <Chip
                    key={i}
                    label={kw}
                    size="small"
                    color="primary"
                    variant="outlined"
                    onDelete={() => removeKeyword(i)}
                  />
                ))}
                <InputBase 
                  inputRef={inputRef}
                  value={newKeyword}
                  onChange={(e) => setNewKeyword(e.target.value)}
                  onKeyDown={addKeyword}
                  placeholder={keywords.length === 0 ? "Ex: pagamento..." : ""} 
                  style={{ flex: 1, minWidth: 100, fontSize: '0.875rem' }}
                />
              </div>
            </Grid>

            <Grid item xs={12} md={6} style={{ display: 'flex', flexDirection: 'column' }}>
              <Label 
                title="Objeções e Baixa Intenção (Esfriam)" 
                description="Palavras que reduzem a temperatura instantaneamente."
              />
              <div 
                className={classes.chipInputContainer} 
                onClick={() => inputRef.current && inputRef.current.focus()}
              >
                {lowKeywords.map((kw, i) => (
                  <Chip
                    key={i}
                    label={kw}
                    size="small"
                    style={{ backgroundColor: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5' }}
                    onDelete={() => removeLowKeyword(i)}
                  />
                ))}
                <InputBase 
                  value={newLowKeyword}
                  onChange={(e) => setNewLowKeyword(e.target.value)}
                  onKeyDown={addLowKeyword}
                  placeholder={lowKeywords.length === 0 ? "Ex: caro, inviável..." : ""} 
                  style={{ flex: 1, minWidth: 100, fontSize: '0.875rem' }}
                />
              </div>
            </Grid>
          </Grid>
        </ConfigSection>

        {/* Seção 2: Ações de Alta Intenção */}
        <ConfigSection 
          icon={Zap} 
          title="2. Ações de Alta Intenção" 
          subtitle="Regras automáticas aplicadas pelo Motor de Scoring ao avaliar o comportamento e o perfil do Lead."
          classes={classes}
        >
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Label 
                title="Enriquecimento de ICP (CRM)" 
                description="Bônus únicos aplicados quando o atendente preenche o perfil do cliente."
              />
              <Box display="flex" flexWrap="wrap" gap="16px" mt={1}>
                <div className={classes.autoRuleCard}>
                  <div className={classes.autoRuleIcon}><CurrencyDollar size={20} weight="bold" /></div>
                  <div><Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>Valor &gt; 0</Typography><Typography variant="caption" color="textSecondary">+15 Pontos</Typography></div>
                </div>
                <div className={classes.autoRuleCard}>
                  <div className={classes.autoRuleIcon}><Briefcase size={20} weight="bold" /></div>
                  <div><Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>Cargo C-Level/Diretor</Typography><Typography variant="caption" color="textSecondary">+20 Pontos</Typography></div>
                </div>
                <div className={classes.autoRuleCard}>
                  <div className={classes.autoRuleIcon}><EnvelopeSimple size={20} weight="bold" /></div>
                  <div><Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>E-mail Corporativo</Typography><Typography variant="caption" color="textSecondary">+15 Pontos</Typography></div>
                </div>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Label 
                title="Comportamento no Chat e Prevenção de Spam" 
                description="Pontuações processadas a cada mensagem recebida."
                mbStyle={{ marginTop: '16px' }}
              />
              <Box display="flex" flexWrap="wrap" gap="16px" mt={1}>
                <div className={classes.autoRuleCard}>
                  <div className={classes.autoRuleIcon} style={{ backgroundColor: '#fef3c7', color: '#d97706' }}><Microphone size={20} weight="bold" /></div>
                  <div><Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>Áudio ou Documento</Typography><Typography variant="caption" color="textSecondary">+10 a +15 Pontos</Typography></div>
                </div>
                <div className={classes.autoRuleCard}>
                  <div className={classes.autoRuleIcon} style={{ backgroundColor: '#dcfce7', color: '#15803d' }}><ChatText size={20} weight="bold" /></div>
                  <div><Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>Msg. Sequenciais</Typography><Typography variant="caption" color="textSecondary">+5 pts por msg ignorada</Typography></div>
                </div>
                <div className={classes.autoRuleCard}>
                  <div className={classes.autoRuleIcon} style={{ backgroundColor: '#fee2e2', color: '#dc2626' }}><ShieldCheck size={20} weight="bold" /></div>
                  <div><Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>Trava de Spam</Typography><Typography variant="caption" color="textSecondary">Limitado a 3x seguidas</Typography></div>
                </div>
              </Box>
            </Grid>
          </Grid>
        </ConfigSection>

        {/* Seção 3: SLAs de Tempo e Follow-up */}
        <ConfigSection 
          icon={Clock} 
          title="3. SLAs de Tempo e Follow-up" 
          subtitle="Define o decaimento da temperatura e cria gatilhos de alerta visual para o time de vendas."
          classes={classes}
        >
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Label 
                title="SLA de Primeira Resposta" 
                description="Em quanto tempo o interesse despenca se o lead não for respondido pela equipe?"
              />
              <TextField 
                type="number" 
                value={config.slaFirstResponse} 
                onChange={(e) => setConfig({ ...config, slaFirstResponse: Number(e.target.value) })}
                fullWidth variant="outlined" size="small" 
                InputProps={{ endAdornment: <InputAdornment position="end">Minutos</InputAdornment> }} 
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Label 
                title="Alerta de Follow-up (Relógio)" 
                description="Tempo de silêncio do lead para exigir follow-up automático."
                mbStyle={{ marginBottom: '29px' }}
              />
              <TextField 
                type="number" 
                value={config.slaFollowUp} 
                onChange={(e) => setConfig({ ...config, slaFollowUp: Number(e.target.value) })}
                fullWidth variant="outlined" size="small" 
                InputProps={{ endAdornment: <InputAdornment position="end">Horas</InputAdornment> }} 
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Label 
                title="Taxa de Perda na Fila (Aguardando)" 
                description="Quantos pontos o lead perde por hora enquanto ninguém o atende?"
              />
              <TextField 
                type="number" 
                value={config.pendingDecayRate} 
                onChange={(e) => setConfig({ ...config, pendingDecayRate: Number(e.target.value) })}
                fullWidth variant="outlined" size="small" 
                InputProps={{ endAdornment: <InputAdornment position="end">Pts/hora</InputAdornment> }} 
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Label 
                title="Taxa de Perda no Atendimento" 
                description="Quantos pontos o lead perde por hora por falta de interação de ambos?"
              />
              <TextField 
                type="number" 
                value={config.commonDecayRate} 
                onChange={(e) => setConfig({ ...config, commonDecayRate: Number(e.target.value) })}
                fullWidth variant="outlined" size="small" 
                InputProps={{ endAdornment: <InputAdornment position="end">Pts/hora</InputAdornment> }} 
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Label 
                title="Esfriamento Crítico" 
                description="Após quanto tempo geral sem responder você considera o lead 'frio'?"
              />
              <TextField 
                select 
                value={config.criticalCooldownHours} 
                onChange={(e) => setConfig({ ...config, criticalCooldownHours: Number(e.target.value) })}
                fullWidth variant="outlined" size="small" SelectProps={{ native: true }} InputLabelProps={{ shrink: true }}
              >
                <option value="1">1 hora(s)</option>
                <option value="2">2 hora(s)</option>
                <option value="4">4 hora(s)</option>
                <option value="8">8 hora(s)</option>
                <option value="12">12 hora(s)</option>
                <option value="24">24 hora(s)</option>
                <option value="48">48 hora(s)</option>
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <Label 
                title="Ghosting (Alerta de Ligação)" 
                description="Quantas msgs do SDR sem resposta geram alerta e aplicam -15 pts?"
                mbStyle={{ marginBottom: '29px' }}
              />
              <TextField 
                type="number" 
                value={config.callAlert} 
                onChange={(e) => setConfig({ ...config, callAlert: Number(e.target.value) })}
                fullWidth variant="outlined" size="small" 
                InputProps={{ endAdornment: <InputAdornment position="end">Mensagens</InputAdornment> }} 
              />
            </Grid>
          </Grid>
        </ConfigSection>

        {/* Seção 4: Modificadores de Contexto */}
        <ConfigSection 
          icon={Tags} 
          title="4. Modificadores de Contexto" 
          subtitle="Ajustes finos baseados no mapeamento visual via Tags ou Colunas do Kanban."
          classes={classes}
        >
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} style={{ display: 'flex', flexDirection: 'column' }}>
              <Label 
                title="Pesos de Tags" 
                description="Associe tags a perda ou ganho de temperatura imediata."
              />
              <div className={classes.tagsRuleContainer}>
                {/* Header */}
                <Box display="flex" justifyContent="space-between" alignItems="center" p={1.5} className={classes.tagsRuleHeader}>
                  <Typography variant="caption" style={{ fontWeight: 'bold', textTransform: 'uppercase', color: theme.palette.type === 'light' ? '#6b7280' : '#9ca3af' }}>Tag</Typography>
                  <Typography variant="caption" style={{ fontWeight: 'bold', textTransform: 'uppercase', color: theme.palette.type === 'light' ? '#6b7280' : '#9ca3af' }}>Modificador</Typography>
                </Box>
                
                {/* Body */}
                <Box flex={1} overflow="auto" p={1} style={{ minHeight: '80px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {(config.tagWeights || []).length === 0 ? (
                    <Typography variant="body2" color="textSecondary" align="center" style={{ marginTop: '16px' }}>
                      Nenhuma regra de tag adicionada.
                    </Typography>
                  ) : (
                    (config.tagWeights || []).map((rule, idx) => (
                      <Box key={idx} display="flex" justifyContent="space-between" alignItems="center" p={1} style={{ backgroundColor: theme.palette.type === 'light' ? '#f9fafb' : '#2d2d2d', borderRadius: 8 }}>
                        <Chip label={rule.name} size="small" style={{ backgroundColor: rule.color, color: '#fff', fontWeight: 'bold', borderRadius: 4 }} />
                        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                          <Typography variant="body2" style={{ fontWeight: 'bold', color: rule.action === 'cool' ? '#dc2626' : (rule.action === 'boil' ? '#ea580c' : theme.palette.text.primary) }}>
                            {rule.action === 'cool' ? '- Esfria Imediatamente' : rule.action === 'boil' ? '+ Ferve Imediatamente' : `${rule.points > 0 ? '+' : ''}${rule.points} Pontos`}
                          </Typography>
                          <IconButton size="small" onClick={() => handleRemoveTagRule(idx)} style={{ padding: 4 }}>
                            <Trash size={16} color="#dc2626" />
                          </IconButton>
                        </Box>
                      </Box>
                    ))
                  )}
                </Box>
                
                {/* Footer */}
                <Box p={1.5} className={classes.tagsRuleFooter}>
                  <Button 
                    size="small" 
                    color="primary" 
                    style={{ fontWeight: 600, textTransform: 'none', padding: 0 }}
                    onClick={handleOpenTagRuleModal}
                  >
                    <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                      <Plus size={16} weight="bold" /> Adicionar Regra de Tag
                    </Box>
                  </Button>
                </Box>
              </div>
            </Grid>

            <Grid item xs={12} md={6}>
              <Label 
                title="Acelerador por Etapa do Funil" 
                description="Em qual coluna o lead tende a esfriar mais rápido e exige atenção?"
              />
              <TextField 
                select 
                value={config.funnelStepAccelerator} 
                onChange={(e) => setConfig({ ...config, funnelStepAccelerator: e.target.value })}
                fullWidth variant="outlined" size="small" SelectProps={{ native: true }} InputLabelProps={{ shrink: true }} style={{ marginTop: '8px' }}
              >
                <option value="nenhuma">Nenhuma (padrão)</option>
                <option value="novo_lead">Novo Lead</option>
                <option value="em_atendimento">Em Atendimento</option>
                <option value="enviou_orcamento">Enviou Orçamento (Acelerado)</option>
                <option value="aguardando_pagamento">Aguardando Pagamento (Acelerado)</option>
              </TextField>

              <Box className={classes.reminderBox}>
                <Typography variant="body2" className={classes.reminderText}>
                  <strong>Lembrete:</strong> Na etapa selecionada, o tempo de esfriamento correrá 2x mais rápido.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </ConfigSection>

        {/* Modal de Regra de Tag */}
        <Dialog open={tagRuleModalOpen} onClose={() => setTagRuleModalOpen(false)} fullWidth maxWidth="xs">
          <DialogTitle>Adicionar Regra de Tag</DialogTitle>
          <DialogContent dividers>
            <Box display="flex" flexDirection="column" style={{ gap: '16px' }}>
              <TextField 
                label="Nome da Tag" 
                value={tagRuleForm.name} 
                onChange={(e) => setTagRuleForm({...tagRuleForm, name: e.target.value})} 
                fullWidth 
                variant="outlined"
                size="small"
              />
              <TextField 
                label="Cor da Tag" 
                type="color" 
                value={tagRuleForm.color} 
                onChange={(e) => setTagRuleForm({...tagRuleForm, color: e.target.value})} 
                fullWidth 
                variant="outlined"
                size="small"
              />
              <TextField 
                select 
                label="Ação da Tag" 
                value={tagRuleForm.action} 
                onChange={(e) => setTagRuleForm({...tagRuleForm, action: e.target.value})} 
                fullWidth 
                variant="outlined"
                size="small"
                SelectProps={{ native: true }} 
                InputLabelProps={{ shrink: true }}
              >
                <option value="points">Adicionar/Remover Pontos</option>
                <option value="boil">Ferve Imediatamente (Quente)</option>
                <option value="cool">Esfria Imediatamente (Frio)</option>
              </TextField>
              
              {tagRuleForm.action === 'points' && (
                <TextField 
                  label="Pontos (Use - para remover)" 
                  type="number" 
                  value={tagRuleForm.points} 
                  onChange={(e) => setTagRuleForm({...tagRuleForm, points: Number(e.target.value)})} 
                  fullWidth 
                  variant="outlined"
                  size="small"
                />
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setTagRuleModalOpen(false)} color="secondary">
              Cancelar
            </Button>
            <Button onClick={handleSaveTagRule} color="primary" variant="contained">
              Adicionar
            </Button>
          </DialogActions>
        </Dialog>

      </div>
    </div>
  );
}
